'use client'
import { useState, useRef, useEffect } from 'react'
import { gsap } from 'gsap'
import { useGSAP } from '@gsap/react'
import { EDITORIAL_BOARD_PER_PAGE } from 'src/_helpers/constants'
import Typography from 'src/components/design-system/Typography'
import { AuthorListItem } from 'src/components/authors/list/AuthorListItem'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'
import Button from 'src/components/design-system/Button'
import LINKS from 'src/_helpers/links'
import useJournalDomainRoutes from 'src/components/journals/domain/useJournalDomainRoutes'
import { buildLocalizedPath } from 'src/_helpers/localizedLinks'
import { getPreferredTranslation } from 'src/_helpers/getPreferredTranslation'
import { orderResourcesByUuid } from 'src/_helpers/orderResourcesByUuid'

gsap.registerPlugin(useGSAP)
const getAffiliations = (author) => {
  return orderResourcesByUuid(author?.affiliations, author?.affiliationOrder)
}
const getAddress = (author) => author?.address
const buildColumnBorders = (index, isDomainJournal = false) => {
  const classes = []
  const tabletBorderClass = isDomainJournal ? 't:border-basic-gray8' : 't:border-dark-blue-dark-blue6'
  const desktopBorderClass = isDomainJournal ? 'd:border-basic-gray8' : 'd:border-dark-blue-dark-blue6'

  if (index % 2 === 1) {
    classes.push('t:border-l', tabletBorderClass, 't:pl-20')
  }

  if (index % 4 !== 0) {
    classes.push('d:border-l', desktopBorderClass, 'd:pl-20')
  }

  return classes.join(' ')
}

export default function EditorialBoardClient({ items, locale, routeContext = 'main' }) {
  const isDomainJournal = routeContext === 'journal-domain'
  const initPage = 1
  const translate = useTranslateEntry()
  const journalRoutes = useJournalDomainRoutes()
  const [currentPage, setCurrentPage] = useState(initPage)
  const newItemsRef = useRef([])

  const totalEditors = items?.reduce((sum, section) => sum + (section.editors?.length || 0), 0) || 0
  const maxToShow = currentPage * EDITORIAL_BOARD_PER_PAGE
  const hasMore = maxToShow < totalEditors

  const displayData = []
  let shownCount = 0

  if (items && items.length > 0) {
    for (const section of items) {
      if (shownCount >= maxToShow) break

      const editorsToShow = Math.min(section.editors.length, maxToShow - shownCount)
      if (editorsToShow > 0) {
        displayData.push({
          ...section,
          editors: section.editors.slice(0, editorsToShow),
        })
        shownCount += editorsToShow
      }
    }
  }

  useEffect(() => {
    newItemsRef.current = newItemsRef.current.slice(0, shownCount)
  }, [shownCount])

  useGSAP(() => {
    if (currentPage > initPage && newItemsRef.current.length > 0) {
      const startIndex = (currentPage - 1) * EDITORIAL_BOARD_PER_PAGE
      const newElements = newItemsRef.current.slice(startIndex)

      gsap.fromTo(
        newElements,
        {
          opacity: 0,
          y: 20,
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          stagger: 0.05,
          ease: 'power2.out',
        },
      )
    }
  }, [currentPage])

  const handleLoadMore = () => {
    setCurrentPage((prev) => prev + 1)
  }

  let globalIndex = 0

  return (
    <div className="pt-64">
      {displayData.length > 0 ? (
        <div className="flex flex-col gap-y-48">
          {displayData.map((section, key) => {
            const translationSection = getPreferredTranslation(section, locale) || {}
            const titleSection = translationSection?.title
            const editors = (section.editors || []).filter((editor) => editor?.data)
            return (
              <div key={key}>
                {titleSection && (
                  <Typography variant="h3-caps-bold" color="text-dark-blue-dark-blue1" className="pb-40">
                    {titleSection}
                  </Typography>
                )}

                <div
                  className={`grid gap-24 border-y ${
                    isDomainJournal ? 'border-basic-gray8' : 'border-dark-blue-dark-blue6'
                  } t:grid-cols-2 d:grid-cols-4 py-24`}
                >
                  {editors.map((editor, i) => {
                    const columnBorders = buildColumnBorders(i, isDomainJournal)
                    const translationEditor = getPreferredTranslation(editor, locale) || {}
                    const position = translationEditor?.title
                    const dataEditor = editor.data
                    const orcid = dataEditor?.orcid
                    const fullname = dataEditor?.fullName
                    const affiliation = getAffiliations(dataEditor)
                    const address = getAddress(dataEditor)
                    const email = dataEditor?.email
                    const resolvedAffiliations = affiliation.length ? affiliation : []
                    const slug = dataEditor?.slug
                    const detailUrl =
                      buildLocalizedPath(
                        routeContext === 'journal-domain' ? journalRoutes.JOURNALS.EDITOR_DETAIL : LINKS.AUTHORS.DETAIL,
                        locale,
                        { slug },
                      ) || ' '
                    const currentGlobalIndex = globalIndex++
                    return (
                      <div
                        key={i}
                        ref={(el) => {
                          newItemsRef.current[currentGlobalIndex] = el
                        }}
                      >
                        <AuthorListItem
                          name={fullname}
                          position={position}
                          orcid={orcid}
                          affiliations={resolvedAffiliations}
                          address={address}
                          email={email}
                          detailUrl={detailUrl}
                          locale={locale}
                          className={`pb-16 ${columnBorders}`}
                        />
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      ) : null}
      {hasMore && (
        <Button
          onClick={handleLoadMore}
          className={`w-fit mt-24 d:mt-56 relative mx-auto block ${
            isDomainJournal
              ? '!border-basic-black !bg-transparent !text-basic-black hover:!bg-basic-gray8'
              : 'bg-transparent hover:bg-basic-white'
          }`}
          size="l"
        >
          <span className="flex items-center gap-x-8">{translate('T_SEE_MORE')}</span>
        </Button>
      )}
    </div>
  )
}
